import * as React from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { searchVirtualProviderTeams } from '../../Api';
import { VirtualProviderTeamDto } from '../../models/dtos/VirtualProviderCatalogDto';

export default function VirtualTeamSearchSelect(props: {
    value: VirtualProviderTeamDto | null;
    onChange: (team: VirtualProviderTeamDto | null) => void;
    disabled?: boolean;
    label?: string;
    sportId?: number;
    excludedTeamIds?: string[];
    /** When set, only teams whose origin country is in this list are offered. `null`/omitted allows every country. */
    allowedCountries?: string[] | null;
}) {
    const [options, setOptions] = React.useState<VirtualProviderTeamDto[]>([]);
    const [inputValue, setInputValue] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (props.disabled) {
            setOptions([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        const timeout = window.setTimeout(() => {
            setLoading(true);
            searchVirtualProviderTeams({ searchTerm: inputValue.trim() || undefined, limit: 20, sportId: props.sportId })
                .then(result => {
                    if (!cancelled) {
                        const excludedTeamIds = new Set(props.excludedTeamIds ?? []);
                        const allowedCountries = props.allowedCountries;
                        setOptions(result.items.filter(team => !excludedTeamIds.has(team.id)
                            && (allowedCountries == null || (team.originCountry != null && allowedCountries.includes(team.originCountry)))));
                    }
                })
                .catch(() => { if (!cancelled) setOptions([]); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, 250);
        return () => {
            cancelled = true;
            window.clearTimeout(timeout);
        };
    }, [inputValue, props.disabled, props.sportId, props.excludedTeamIds, props.allowedCountries]);

    return <Autocomplete
        fullWidth
        disabled={props.disabled}
        options={options}
        value={props.value}
        inputValue={inputValue}
        onInputChange={(_, value) => setInputValue(value)}
        onChange={(_, value) => props.onChange(value)}
        filterOptions={items => items}
        getOptionLabel={team => `${team.name}${team.originCountry ? ` (${team.originCountry})` : ''}`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={loading}
        renderInput={params => <TextField
            {...params}
            label={props.label ?? 'Specific team'}
            InputProps={{
                ...params.InputProps,
                endAdornment: <>{loading ? <CircularProgress color="inherit" size={18} /> : null}{params.InputProps.endAdornment}</>,
            }}
        />}
    />;
}
